import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ResourceLayout, ResourceHero, ResourceContactCTA, CodeBlock,
  DownloadButton, DownloadRow, StatusBlock, SignatureFooter,
  H2, ACCENTS, MONO, fade,
} from './_shared'
import { track } from '../../lib/track'

const A = ACCENTS.blue

const GROUPS: { title: string; items: string[] }[] = [
  {
    title: 'Fundamento técnico',
    items: [
      'robots.txt libera o crawl das páginas certas (e bloqueia o que não deve indexar)',
      'sitemap.xml nativo (WP 5.5+) gerado e enviado no Google Search Console',
      'URLs limpas com keyword no slug (sem ?p=123)',
      'HTTPS em tudo + canonical em toda página',
      'hreflang configurado se o site for multi-idioma',
    ],
  },
  {
    title: 'Structured Data (JSON-LD)',
    items: [
      'Organization ou LocalBusiness no home',
      'BreadcrumbList nas páginas internas',
      'Article / BlogPosting nos posts',
      'FAQPage onde houver perguntas e respostas',
      'Product + AggregateRating se for e-commerce ou tiver reviews',
      'tudo validado no Rich Results Test do Google',
    ],
  },
  {
    title: 'On-page',
    items: [
      '1 H1 por página, com a keyword principal',
      'H2 escritos como perguntas naturais (ganha em SEO e em GEO/IA)',
      'title 50-60 caracteres, meta description 150-160',
      'alt descritivo em toda imagem',
      'internal linking em hub-and-spoke (pilar puxa autoridade dos satélites)',
    ],
  },
  {
    title: 'Core Web Vitals',
    items: [
      'LCP < 2,5s — preload do hero, sem lazy load no primeiro contentful',
      'CLS < 0,1 — dimensões fixas em imagem, embed e fonte',
      'INP < 200ms — corta JS desnecessário, defer no que não é crítico',
      'testado em mobile primeiro (é onde está 80% do tráfego)',
      'cache em camadas (página + objeto + opcache) + CDN na borda',
    ],
  },
]

const JSON_LD = `<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Sua Empresa",
  "url": "https://seudominio.com.br",
  "telephone": "+55 11 90000-0000",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "São Paulo",
    "addressRegion": "SP",
    "addressCountry": "BR"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "187"
  }
}
</script>`

const CHECKLIST_MD = `# Checklist de SEO técnico

## 1. Fundamento técnico
- [ ] robots.txt libera crawl das páginas certas
- [ ] sitemap.xml nativo (WP 5.5+) enviado no GSC
- [ ] URLs limpas com keyword no slug
- [ ] HTTPS + canonical em toda página
- [ ] hreflang se multi-idioma

## 2. Structured Data (JSON-LD)
- [ ] Organization / LocalBusiness no home
- [ ] BreadcrumbList nas internas
- [ ] Article / BlogPosting nos posts
- [ ] FAQPage onde houver perguntas
- [ ] Product + AggregateRating se aplicável
- [ ] validado no Rich Results Test

## 3. On-page
- [ ] 1 H1 com keyword por página
- [ ] H2 como perguntas naturais (SEO + GEO)
- [ ] title 50-60 / meta description 150-160
- [ ] alt em toda imagem
- [ ] internal linking hub-and-spoke

## 4. Core Web Vitals
- [ ] LCP < 2,5s (preload hero)
- [ ] CLS < 0,1 (dimensões fixas)
- [ ] INP < 200ms (corta JS)
- [ ] mobile-first
- [ ] cache em camadas + CDN
`

function Checklist() {
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const toggle = (id: string) => {
    setChecked((c) => {
      const next = { ...c, [id]: !c[id] }
      if (!c[id]) track('recurso_check', { resource: 'seo-checklist', item: id })
      return next
    })
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {GROUPS.map((g, gi) => (
        <motion.div key={gi} {...fade(0.05 + gi * 0.04)} style={{
          background: 'linear-gradient(180deg, #111, #0d0d0d)',
          border: '1px solid #1e1e1e',
          borderLeft: `3px solid ${A.solid}`,
          borderRadius: 10,
          padding: '1.2rem 1.4rem',
        }}>
          <p style={{ fontFamily: MONO, fontSize: '0.74rem', color: A.text, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.9rem' }}>
            <span style={{ color: '#555' }}>{`0${gi + 1} // `}</span>{g.title}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
            {g.items.map((item, ii) => {
              const id = `${gi}-${ii}`
              const on = !!checked[id]
              return (
                <label key={ii} onClick={() => toggle(id)} style={{
                  display: 'flex', gap: '0.65rem', alignItems: 'flex-start',
                  cursor: 'pointer', fontSize: '0.86rem', lineHeight: 1.5,
                  color: on ? '#666' : '#ddd',
                  textDecoration: on ? 'line-through' : 'none',
                }}>
                  <span style={{
                    flexShrink: 0, width: 18, height: 18, marginTop: 2, borderRadius: 4,
                    border: `1.5px solid ${on ? A.solid : '#333'}`,
                    background: on ? A.solid : 'transparent',
                    color: '#0a0a0a', fontSize: '0.7rem', fontWeight: 900,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{on ? '✓' : ''}</span>
                  <span>{item}</span>
                </label>
              )
            })}
          </div>
        </motion.div>
      ))}
    </div>
  )
}

export default function SeoChecklist() {
  return (
    <ResourceLayout>
      <ResourceHero
        tag="recurso · seo · schema"
        title="Checklist de SEO técnico: o que o WordPress te dá de graça"
        desc="21 itens divididos em 4 frentes (fundamento, structured data, on-page, Core Web Vitals). É o que checo antes de dizer que um site está pronto pra ranquear. Marca conforme implementa — fica salvo no seu navegador."
        accent={A}
        source="seo-checklist"
        word="SCHEMA"
      />

      <StatusBlock accent={A} rows={[
        { k: 'tipo', v: 'seo · checklist técnico' },
        { k: 'formato', v: '21 itens interativos + exemplo JSON-LD pronto' },
        { k: 'esforço', v: 'varia — uns 5min cada item' },
        { k: 'valor', v: 'cobre crawl, schema, on-page e performance' },
      ]} />

      <motion.section {...fade(0.06)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>o checklist</H2>
        <DownloadRow>
          <DownloadButton content={CHECKLIST_MD} filename="seo-checklist.md" accent={A} source="seo-checklist" mime="text/markdown" />
        </DownloadRow>
        <Checklist />
      </motion.section>

      <motion.section {...fade(0.14)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>exemplo de JSON-LD (LocalBusiness)</H2>
        <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '1rem' }}>
          Cola no <code style={{ fontFamily: MONO, background: '#141414', padding: '0.1rem 0.35rem', borderRadius: 3, fontSize: '0.78rem', color: A.text }}>&lt;head&gt;</code> ou deixa o Yoast/RankMath gerar. Depois valida no Rich Results Test do Google.
        </p>
        <DownloadRow>
          <DownloadButton content={JSON_LD} filename="local-business.jsonld" accent={A} source="seo-checklist-jsonld" mime="application/ld+json" />
        </DownloadRow>
        <CodeBlock lang="json-ld" accent={A} source="seo-checklist">{JSON_LD}</CodeBlock>
      </motion.section>

      <ResourceContactCTA accent={A} source="seo-checklist" />
      <SignatureFooter accent={A} />
    </ResourceLayout>
  )
}
